import React, { useMemo } from "react";
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  AreaSeries,
  LineSeries,
  Tooltip,
  Legend,
  Inject,
} from "@syncfusion/ej2-react-charts";
import { formatNumber } from "../../utils/formatNumber";

export default function AvgActiveEpgChart({
  minuteSeries = [],
  programSeries = [],
  title = "Average Active TV",
  // Minutes past this one have not happened yet: on today's chart the rest of
  // the array is zeroes waiting to be filled, not an audience of nobody.
  lastMinute = 1439,
}) {

  const storedUser = JSON.parse(
    localStorage.getItem("adm_panel_user") || "{}"
  );

  const isDark = !!storedUser.darkmode;
  const axisTextColor = isDark ? "#ffffff" : "#666666";

  const slicedSeries = useMemo(() => {

    if (!minuteSeries.length) return [];

    /* PROGRAM LOOKUP (hangi dakikada program var) */
    const programLookup = new Map();

    programSeries.forEach(p => {
      for (let i = p.startMinute; i <= p.endMinute; i++) {
        programLookup.set(i, true);
      }
    });

    const series = [];

    /* MAIN DATA (legend'e girmeyecek) */
    const recorded = (m) => m.minute <= lastMinute;

    const mainData = minuteSeries.map(m => ({
      minute: m.minute,
      value: programLookup.has(m.minute) || !recorded(m) ? null : m.value
    }));

    series.push({
      type: "area",
      name: "Main Data",
      color: "#b0b0b0",
      backgroundColor: "rgba(176,176,176,0.35)",
      data: mainData,
      showInLegend: false
    });



      const programGroups = {};

      programSeries.forEach(p => {
        if (!programGroups[p.name]) {
          programGroups[p.name] = [];
        }
        programGroups[p.name].push(p);
      });

      /* HER PROGRAM İÇİN TEK SERIES */
      Object.values(programGroups).forEach(group => {

        const programName = group[0].name;
        const programColor = group[0].color;

        const programData = minuteSeries.map((m) => {

          const inRange = group.some(p =>
            m.minute >= p.startMinute && m.minute <= p.endMinute
          );

          return {
            minute: m.minute,
            value: inRange && recorded(m) ? m.value : null
          };

        });

        // One average per airing, not per title. Grouping by name put a single
        // number across every showing of a programme, so a title that airs at
        // breakfast and again at peak had the same line drawn over both — far
        // too high on one band, far too low on the other.
        const averages = group.map(p => {
          let sum = 0;
          let count = 0;
          for (const m of minuteSeries) {
            if (m.minute >= p.startMinute && m.minute <= p.endMinute && recorded(m)) {
              sum += m.value;
              count++;
            }
          }
          return count > 0 ? Math.round(sum / count) : null;
        });

        const avgLine = minuteSeries.map(m => {

          const idx = group.findIndex(p =>
            m.minute >= p.startMinute && m.minute <= p.endMinute
          );

          return {
            minute: m.minute,
            value: idx === -1 ? null : averages[idx]
          };

        });

        const areaColor = programColor + "80";

        series.push({
          type: "area",
          name: programName,
          color: programColor,
          backgroundColor: areaColor,
          data: programData
        });

        series.push({
          type: "line",
          name: `Avg ${programName}`,
          color: programColor,
          data: avgLine
        });

      });

          return series;

  }, [minuteSeries, programSeries, lastMinute]);


  const { yAxisMax, xAxisMin, xAxisMax } = useMemo(() => {

    const values = slicedSeries
      .flatMap(s => s.data)
      .map(d => d.value)
      .filter(v => v !== null);

    const max = Math.max(...values, 0);

    const minutes = minuteSeries
      .map(m => m.minute)
      .filter(m => m !== undefined);

    return {
      yAxisMax: max > 0 ? Math.ceil(max * 1.15) : 10,
      xAxisMin: Math.min(...minutes),
      xAxisMax: Math.max(...minutes)
    };

  }, [slicedSeries, minuteSeries]);


  const handleAxisLabelRender = (args) => {

    if (args.axis?.name === "primaryYAxis") {
      const value = parseFloat(args.value);
      if (!isNaN(value)) {
        args.text = formatNumber(value);
      }
      return;
    }

    if (args.axis?.name === "primaryXAxis") {

      const minutes = parseFloat(args.value);
      if (!isNaN(minutes)) {

        const h = Math.floor(minutes / 60);
        const m = Math.floor(minutes % 60);

        args.text =
          `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}`;

      }

    }

  };


  const handleTooltipRender = (args) => {

    if (!args.data || args.data.pointX === undefined) return;

    const minutes = args.data.pointX;

    const h = Math.floor(minutes / 60);
    const m = Math.floor(minutes % 60);

    const time =
      `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}`;

    args.text =
      `${time}<br/>${args.series.name}<br/>Value: ${formatNumber(args.data.pointY)}`;

  };


  return (
    <div className="chart-container">

      <h3 className="chart-title">{title}</h3>

      {!minuteSeries.length && (
        <p style={{ padding: 20, color: "#666" }}>
          No minute data available
        </p>
      )}

      {minuteSeries.length > 0 && (
        <ChartComponent
          theme="Material"
          background={isDark ? "#1e293b" : "#ffffff"}
          width="100%"
          height="480px"
          margin={{ left: 50, right: 50, top: 20, bottom: 60 }}

          legendRender={(args) => {
            if (args.text === "Main Data") {
              args.cancel = true;
            }
          }}
          primaryXAxis={{
            name: "primaryXAxis",
            valueType: "Double",
            minimum: xAxisMin,
            maximum: xAxisMax,
            interval: 60,
            labelFormat: "{value}",
            majorGridLines: { width: 0.5, color: "#e0e0e0" },
            majorTickLines: { width: 0 },
            labelIntersectAction: "Rotate45",
            labelStyle: {
              fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
              fontSize: "11px",
              color: axisTextColor
            }
          }}

          primaryYAxis={{
            name: "primaryYAxis",
            minimum: 0,
            maximum: yAxisMax,
            majorTickLines: { width: 0 },
            lineStyle: { width: 0 },
            labelFormat: "n0",
            labelStyle: {
              fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
              color: axisTextColor
            }
          }}

          tooltip={{
            enable: true,
            shared: false,
            fill: "#0f172a",
            border: { color: "#334155", width: 1 },
            textStyle: {
              color: "#e5e7eb",
              fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
              size: "13px"
            }
          }}

          legendSettings={{
            visible: true,
            position: "Top",
            padding: 10,
            alignment: "Center",
            textWrap: "Wrap",
            maximumLabelWidth: 200,
            toggleVisibility: true,
            textStyle: {
              fontFamily: "Inter, system-ui, -apple-system",
              fontSize: "12px",
              fontWeight: "500",
              color: isDark ? "#e5e7eb" : "#374151"
            }
          }}

          axisLabelRender={handleAxisLabelRender}
          tooltipRender={handleTooltipRender}
          enableSideBySidePlacement={false}
        >

          <Inject services={[
            AreaSeries,
            LineSeries,
            Tooltip,
            Legend
          ]} />

          <SeriesCollectionDirective>

          {slicedSeries.map((s, idx) => {

            const isMainData = s.name === "Main Data";

            if (s.type === "area") {

              return (
                <SeriesDirective
                  key={`${s.name}-area-${idx}`}
                  type="Area"
                  dataSource={s.data}
                  xName="minute"
                  yName="value"
                  name={s.name}
                  fill={s.backgroundColor || s.color}
                  border={{ width: 2, color: s.color }}
                  opacity={1}
                  animation={{ enable: true, duration: 400 }}
                  legendShape="Rectangle"
                  showInLegend={!isMainData}
                />
              );

            }

            return (
              <SeriesDirective
                key={`${s.name}-line-${idx}`}
                type="Line"
                dataSource={s.data}
                xName="minute"
                yName="value"
                name={s.name}
                fill={s.color}
                width={2}
                dashArray="5,5"
                animation={{ enable: true, duration: 400 }}
                legendShape="HorizontalLine"
                showInLegend={!isMainData}
              />
            );

          })}

          </SeriesCollectionDirective>

        </ChartComponent>
      )}

    </div>
  );
}